/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import styles from './styles';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { ContentCopyOutlined, DeleteRounded, EditOutlined, SaveRounded } from '@mui/icons-material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useDispatch } from 'react-redux';
import { strippingTableColumns } from './table_config';
import { CONTROL_TABLE_PARAMETER_MAP, HIDE_TARGET_ECD, HYDRAULICS_TRIP_PIPE_TYPE, HYDRAULICS_TRIP_PIPE_TYPE_VALUE, MAX_ROW_LIMITS, STRIPPING_REPORT_EVERY_US } from '../../../../utils/constants';
import DeleteConfirmationModal from '../../../deleteConfirmationModal';
import { COLORS } from '../../../../shared/config/colors';
import Datagrid from '../../../ui/Datagrid';
import { renderCell } from '../../../assetData/table_config';
import SelectEditValueTypes from '../../../ui/SelectEditValueTypes';
import SelectEditInputTextField from '../../../ui/SelectEditInputTextField';
import SelectDrillDetails from '../../../ui/SelectDrillDetails';
import { enqueueSnackbar } from '../../../../store/actions/snackbar';
import { isMobile } from 'react-device-detect';
import MobileStrippingRunInput from '../../../mobile/hydraulics/featuredWellRunInputs/strippingRunInput';
import MobileSelectDrillDetails from '../../../mobile/hydraulics/SelectDrillDetails';
import MobileSelectEditInputTextField from '../../../mobile/hydraulics/SelectEditInputTextField';
import MobileSelectEditValueTypes from '../../../mobile/hydraulics/SelectEditValueTypes';
import WellboreTargetDialog from '../WellboreRunInputs/WellboreTargetDialog';
import MobileWellboreTargetDialog from '../../../mobile/hydraulics/targetDialog';
function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}
const StrippingInputs = ({
  hydraulicsLanding,
  featuredWellRun,
  proxyLoader,
  strippingData,
  setStrippingData,
  setParameterDirty,
  hydraulicsRunUnits
}) => {
  const classes = styles();
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [scrollToNew, setScrollToNew] = useState(false);
  const strippingUnits = hydraulicsLanding?.units?.stripping.inputs;
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [anchorPoint, setAnchorPoint] = useState('None');
  const [targetECD, setTargetECD] = useState('');
  const [targetData, setTargetData] = useState({});
  const [selectedRow, setSelectedRow] = useState({});

  useEffect(() => {
    if (targetData?.anchor_point && selectedRow?._id) {
      setProperties(prev =>
        prev.map(item =>
          item._id === selectedRow._id
            ? {
                ...item,
                target_data: targetData,
                isDirty: true
              }
            : item
        )
      );
    }
  }, [targetData]);

  const transformRunsECDSpan = (targetData) => {
    if (!targetData.ecd_span) return [];
    return targetData?.ecd_span?.map((row) => ({
      name: row?.name,
      low_emw: Number(row.low_emw),
      high_emw: Number(row.high_emw),
    }));
  };

  const transformControlsTable = (targetData) => {
    if (!targetData.controls_table) return [];
    return targetData?.controls_table?.map((row) => ({
      maintain: row?.parameter?.toLowerCase() === 'sbp' ? '-' : row?.maintain?.toLowerCase(),
      parameter: CONTROL_TABLE_PARAMETER_MAP[row?.parameter] || row?.parameter,
      from: Number(row.from),
      to: Number(row.to),
      step_size: row?.parameter?.toLowerCase() === 'sbp' ? '-' : Number(row.step_size),
    }));
  };

  const getPipeConfig = (configValue) => {
    return Object.keys(HYDRAULICS_TRIP_PIPE_TYPE_VALUE).find(
      (key) => HYDRAULICS_TRIP_PIPE_TYPE_VALUE[key] === configValue
    ) || configValue;
  };

  useEffect(() => {
    if (Array.isArray(strippingData)) {
      const { drillFluids = [] } = hydraulicsLanding?.data || [];
      let hasDeletedFillingFluid = false;
      const formatFluid = (fluid) =>
        `${fluid.name.trim()} @ ${fluid.MD.toFixed(hydraulicsLanding?.units?.drillFluids?.MD.decimals)}${hydraulicsLanding?.units?.drillFluids?.MD.label}`;
      const stripData = strippingData.map((item, index) => {
        let report_interval;
        const isValidInterval = STRIPPING_REPORT_EVERY_US.includes(item?.report_interval);
        if (!isValidInterval) {
          report_interval = STRIPPING_REPORT_EVERY_US[2];
          item.isDirty = true;
          setInvalidRows(prev => ({ ...prev, [item._id]: true }));
          dispatch(
            enqueueSnackbar(
              'Configured interval not found in the list. A default has been applied, please review and save.',
              'error',
              new Date().getTime() + Math.random()
            )
          );
        } else {
          report_interval = item?.report_interval;
        }
        if (!item.filling_fluid_id) {
          item.filling_fluid_id = hydraulicsLanding?.data?.drillFluids[0].id;
          item.isDirty = true;
        }
        const fillingFluidExists = drillFluids.some(fluid => fluid.id === item.filling_fluid_id);
        if (!fillingFluidExists) {
          hasDeletedFillingFluid = true;
          item.isDirty = true;
          item.filling_fluid_id = hydraulicsLanding?.data?.drillFluids[0].id;
        }
        const drillFillingFluid = drillFluids.find(drillItem => drillItem.id === item.filling_fluid_id);
        return {
          ...item,
          runNumber: index + 1,
          tubular: getPipeConfig(item.tubular),
          report_interval,
          filling_fluid_id: drillFillingFluid ? formatFluid(drillFillingFluid) : '',
          target_data: item?.target_data || {}
        };
      });

      if (hasDeletedFillingFluid) {
        dispatch(
          enqueueSnackbar(
            'Configured Drillfluid in Stripping is not found, Please Check',
            'error',
            new Date().getTime() + Math.random()
          )
        );
      }
      setProperties(stripData);
    }
  }, [strippingData]);

  useEffect(() => {
    if (properties[0]?.from === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'from', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'from');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const addNewStrippingRow = () => {
    const nextId = properties.length;
    const newRow = {
      from: '',
      to: '',
      tubular: 'Drillstring',
      speed: '',
      report_interval: 500,
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true,
      runNumber: properties.length + 1,
      filling_fluid_id: '',
      target_data: {
        anchor_point: 'None',
        target_ecd: null
      }
    };
    setProperties((prev) => [...prev, newRow]);
  };

  const handleAddParameter = () => {
    if (properties[0]?.isNewRow) return 0;
    addNewStrippingRow();
    setScrollToNew(true);
  };

  const updateData = (id, field, value) => {
    const updateFieldValue = (item) => {
      const isDirty = value !== item[field];
      return {
        ...item,
        [field]: ['speed', 'from', 'to', 'report_interval'].includes(field)
          ? Number(value)
          : value,
        isDirty,
      };
    };

    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item =>
        item._id === id ? updateFieldValue(item) : item
      );
      return updatedProperties;
    });
  };

  const formatStrippingData = (properties, drillFluids) =>
    properties.map((item) => {
      const matchedFluid = drillFluids?.find((fluid) =>
      `${fluid?.name?.trim()} @ ${fluid?.MD.toFixed(
        hydraulicsLanding?.units?.drillFluids?.MD.decimals
      )}${hydraulicsLanding?.units?.drillFluids?.MD.label}` ===
      item?.filling_fluid_id
      );

      return {
        _id: item._id,
        from: item.from,
        to: item.to,
        speed: item.speed,
        report_interval: item.report_interval,
        tubular: HYDRAULICS_TRIP_PIPE_TYPE_VALUE[item?.tubular] ?? item.tubular,
        filling_fluid_id: matchedFluid?.id ?? null,
        target_data: item?.target_data
      };
    });

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const validateStrippingDetails = (details) => {
    const { _id, speed, from, to } = details;
    const min_speed = strippingUnits.speed.min;
    const max_speed = strippingUnits.speed.max;
    const min_from = strippingUnits.from.min;
    const min_to = strippingUnits.to.min;
    const holeDepth = featuredWellRun?.run_details?.hole_depth;
    const unitLabel = strippingUnits?.to?.label;
    if (speed < min_speed || speed > max_speed) return showError(`Speed should be between ${min_speed} and ${max_speed} ${strippingUnits.speed?.label}`, _id);
    if (from < min_from || from > holeDepth) {
      return showError(
        `From MD should be between 0 and ${holeDepth} ${unitLabel}.`,
        _id
      );
    }

    if (to < min_to || to > holeDepth) {
      return showError(
        `To MD should be between 0 and ${holeDepth} ${unitLabel}.`,
        _id
      );
    }

    if (from === to) {
      return showError(
        'From MD and To MD should not be the same.',
        _id
      );
    }

    return true;
  };

  const checkStrippingValues = (value) => {
    const row = value;
    const invalidFields = [];
    const isInvalidNumber = (val) => {
      // eslint-disable-next-line no-mixed-operators
      return isNaN(val) || val === '' || typeof val === 'string' && val.trim() === '';
    };

    if (row?.from === undefined || row?.from === null || isInvalidNumber(row.from)) {
      invalidFields.push('From MD');
    }
    if (row?.to === undefined || row?.to === null || isInvalidNumber(row.to)) {
      invalidFields.push('To MD');
    }
    if (row?.speed === undefined || row?.speed === null || isInvalidNumber(row.speed)) {
      invalidFields.push('Speed');
    }

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return validateStrippingDetails(row);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const checkForDuplicates = (properties) => {
    const seen = new Set();
    for (let i = 0; i < properties.length; i++) {
      const { filling_fluid_id, to, from, report_interval, speed, tubular, target_data } = properties[i];
      const targetLabel = `${(!target_data || !target_data?.anchor_point || target_data?.anchor_point === 'None') ? 'None' : target_data?.anchor_point}@${target_data?.target_ecd}`;

      const key = `${from}|${to}|${filling_fluid_id}|${report_interval}|${speed}|${tubular}${targetLabel}`;
      if (seen.has(key)) {
        setInvalidRows(prev => ({ ...prev, [properties[i]?._id]: true }));
        dispatch(
          enqueueSnackbar(
            'Row already exisit',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return false;
      }
      seen.add(key);
    }
    return true;
  };

  const saveStripping = (row) => {
    if (checkStrippingValues(row) && checkForDuplicates(properties)) {
      let target_data;
      if (row?.target_data && Object.keys(row?.target_data).length !== 0) {
        const updatedRunsECDSpan = transformRunsECDSpan(row?.target_data);
        const updatedControlsTable = transformControlsTable(row?.target_data);
        target_data = {
          anchor_point: row?.target_data?.anchor_point,
          target_ecd: row?.target_data?.target_ecd == null
            ? null
            : Number(row?.target_data?.target_ecd),
          ecd_span: updatedRunsECDSpan,
          controls_table: updatedControlsTable
        };
      }
      const updated = properties.map((item) =>
        item._id === row._id
          ? { ...item, target_data, isNewRow: false, isDirty: false }
          : item
      );
      setProperties(updated);
      setStrippingData(formatStrippingData(updated, hydraulicsLanding?.data?.drillFluids));
      setParameterDirty(prev => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const updated = [...prev];
        updated[0] = { ...updated[0], isDirty: true };
        return updated;
      });
      return updated;
    }
  };

  const removeStripping = (row) => {
    setProperties((prev) => {
      const updated = prev.filter((item) => item._id !== row._id);

      setStrippingData(formatStrippingData(updated, hydraulicsLanding?.data?.drillFluids));
      return updated;
    });

    setDeleteAction(false);
    setDeleteRow({});
    setParameterDirty(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], isDirty: true };
      return updated;
    });
  };

  const copyStripping = (row) => {
    setProperties((prev) => {
      const newRow = {
        ...row,
        runNumber: prev.length + 1,
        _id: generateRandom(),
        isNewRow: true,
        isDirty: true,
      };
      const updated = [...prev, newRow];
      return updated;
    });
  };

  const renderActionCell = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || proxyLoader}
          onClick={() => {
            saveStripping(row);
          }}
        >
          <SaveRounded />
        </IconButton>
       <IconButton
          variant="solid"
          disabled={properties.some(item => item.isDirty === true || item.isNewRow || properties?.length >= 5) || proxyLoader}
          onClick={() => {
            copyStripping(row);
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={proxyLoader}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const renderTargetsEditAction = ({ row }) => {
    return (
        <Grid container justifyContent="center">
          <Grid item>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                variant="solid"
                onClick={() => {
                  if (row?.target_data?.anchor_point) {
                    setAnchorPoint(row?.target_data?.anchor_point);
                    setTargetECD(row?.target_data?.target_ecd);
                    setTargetData(row?.target_data);
                  }
                  setShowTargetModal(true);
                  setTimeout(() => {
                    setSelectedRow(row);
                  }, 100);
                }}
              >
                <EditOutlined sx={{ fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>
              {!row?.target_data?.anchor_point || HIDE_TARGET_ECD.includes(row?.target_data?.anchor_point)
                ? (
                  <Typography variant="button" fontSize={{ xs: '0.75rem', sm: '1rem' }} fontWeight={900}>None</Typography>
                  )
                : (
                  <Box display="flex" flexDirection="column" textAlign='center'>
                    <Typography variant="button" fontSize={{ xs: '0.75rem', sm: '1rem' }} lineHeight={1.2} fontWeight={900}>
                      {row?.target_data?.target_ecd} {hydraulicsRunUnits?.inputs?.target_data?.target_ecd?.label}
                    </Typography>
                    <Typography variant="button" fontSize={{ xs: '0.75rem', sm: '1rem' }} lineHeight={1.2} fontWeight={900}>
                      @ {row?.target_data?.anchor_point}
                    </Typography>
                  </Box>
                  )}
            </Box>
          </Grid>
        </Grid>
    );
  };

  const handleAnchorPointTargetECD = (newAnchorPoint, newTargetECD) => {
    setAnchorPoint(newAnchorPoint);
    setTargetECD(newTargetECD);
  };

  const updateTargetData = (data) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === data?._id) {
          return {
            ...item,
            isDirty: true,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderTripPipeTypeCell = (params) => {
    if (isMobile) {
      return (
         <MobileSelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={HYDRAULICS_TRIP_PIPE_TYPE}
        loading={proxyLoader}
      />
      );
    }
    return (
      <SelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={HYDRAULICS_TRIP_PIPE_TYPE}
      />
    );
  };

  const renderReportEveryCell = (params) => {
    if (isMobile) {
      return (
         <MobileSelectEditValueTypes
        {...params}
        enableSort={false}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={STRIPPING_REPORT_EVERY_US}
        loading={proxyLoader}
      />
      );
    }
    return (
      <SelectEditValueTypes
        {...params}
        enableSort={false}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={STRIPPING_REPORT_EVERY_US}
      />
    );
  };

  const renderValueCell = (params) => {
    if (isMobile) {
      return (
        <MobileSelectEditInputTextField numberFormatOnly {...params} updateData={updateData} loading={proxyLoader} />
      );
    }
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderDrillFluidCell = (params) => {
    const drillFluids = hydraulicsLanding?.data?.drillFluids || [];
    const drillFluidsWithAir = [...drillFluids];
    if (isMobile) {
      return (
        <MobileSelectDrillDetails
          {...params}
          closeCellAfterSelect={true}
          updateData={updateData}
          data={drillFluidsWithAir}
          label={'ft'}
          decimals={2}
          loading={proxyLoader}
        />
      );
    }
    return (
      <SelectDrillDetails
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={drillFluidsWithAir}
        label={hydraulicsLanding?.units?.drillFluids?.MD.label}
        decimals={hydraulicsLanding?.units?.drillFluids?.MD.decimals}
      />
    );
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <>
      <Grid container>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" marginLeft={'13px'}>
            <Typography
              variant="button"
              fontWeight="700"
              onClick={handleAddParameter}
              className={
                properties?.length === MAX_ROW_LIMITS.STRIPPING || properties.some(item => item.isDirty === true)
                  ? classes.disableIcon
                  : ''
              }
              sx={{ cursor: 'pointer' }}
            >
              + Add
            </Typography>

            <Typography
              variant='h6'
              color={COLORS.LIGHT_GREY}
              fontWeight="500"
              fontSize={isMobile ? '15px' : '20px'}
              sx={{ marginLeft: '12px' }}
            >
              {`(Note: Maximum ${MAX_ROW_LIMITS.STRIPPING} entries are allowed)`}
            </Typography>
          </Box>
        </Box>
        <Grid item xs={12}>
          {isMobile
            ? (
              <MobileStrippingRunInput
              data={properties}
              columns={strippingTableColumns(renderValueCell, renderActionCell, renderTripPipeTypeCell, renderReportEveryCell, renderCell, renderDrillFluidCell, strippingUnits, renderTargetsEditAction)}
              updateData={updateData}
              loading={proxyLoader}
              scrollToNew={scrollToNew}
              onScrolled={() => setScrollToNew(false)}
            />
              )
            : (
            <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900,
                textAlign: 'justify',
                pointerEvents: proxyLoader ? 'none' : 'auto',
                backgroundColor: proxyLoader ? '#f5f5f5' : 'inherit'
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem !important',
                fontWeight: '900 !important'
              },
            }}
            loading={proxyLoader}
            columns={strippingTableColumns(renderValueCell, renderActionCell, renderTripPipeTypeCell, renderReportEveryCell, renderCell, renderDrillFluidCell, strippingUnits, renderTargetsEditAction)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            hideFooterPagination
            hideFooterSelectedRowCount
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            height={'25vh'}
            isRowSelectable={() => !proxyLoader}
            getRowClassName={(params) =>
              proxyLoader ? 'Mui-disabled' : (invalidRows[params.id] ? classes.invalidRow : '')
            }
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
          />
              )
          }

        </Grid>
      </Grid>
      {
        deleteAction && <DeleteConfirmationModal
          open={deleteAction}
          handleDelete={removeStripping}
          handleClose={setDeleteAction}
          deleteRow={deleteRow}
        />
      }
      {showTargetModal
        ? <>
          {isMobile
            ? (
              <MobileWellboreTargetDialog
                updateTargetData={updateTargetData}
                data={featuredWellRun?.run_details}
                anchorPoint={anchorPoint}
                targetECD={targetECD}
                setTargetData={setTargetData}
                isOpen={showTargetModal}
                setAnchorPoint={setAnchorPoint}
                setTargetECD={setTargetECD}
                onSave={handleAnchorPointTargetECD}
                onCloseClick={() => {
                  setAnchorPoint('None');
                  setTargetECD(null);
                  setShowTargetModal(false);
                  setTimeout(() => {
                    setTargetData({});
                    setSelectedRow({});
                  }, 100);
                }}
                hydraulicsRunUnits={hydraulicsRunUnits}
                targetData={targetData}
              />
              )
            : (
              <WellboreTargetDialog
                updateTargetData={updateTargetData}
                data={featuredWellRun?.run_details}
                anchorPoint={anchorPoint}
                targetECD={targetECD}
                setTargetData={setTargetData}
                isOpen={showTargetModal}
                setAnchorPoint={setAnchorPoint}
                setTargetECD={setTargetECD}
                onSave={handleAnchorPointTargetECD}
                onCloseClick={() => {
                  setAnchorPoint('None');
                  setTargetECD(null);
                  setShowTargetModal(false);
                  setTimeout(() => {
                    setTargetData({});
                    setSelectedRow({});
                  }, 100);
                }}
                hydraulicsRunUnits={hydraulicsRunUnits}
                targetData={targetData}
              />
              )}
        </>
        : null
      }
    </>
  );
};
StrippingInputs.propTypes = {
  hydraulicsLanding: PropTypes.object,
  featuredWellRun: PropTypes.object,
  proxyLoader: PropTypes.bool,
  strippingData: PropTypes.any,
  setStrippingData: PropTypes.func,
  setParameterDirty: PropTypes.any,
  hydraulicsRunUnits: PropTypes.any
};
export default StrippingInputs;
